import type { ReactNode } from 'react';
import { usePermissions } from '../../context/PermissionsContext';

export interface PermissionGateProps {
  /** The permission string required to show children. */
  permission: string;
  /** Content to render when the user has the permission. */
  children: ReactNode;
  /** Optional content to render when the user lacks the permission. Defaults to null. */
  fallback?: ReactNode;
}

/**
 * Renders `children` only when the current user has the required permission.
 * Shows `fallback` (default: nothing) otherwise.
 *
 * Use this to hide buttons, sections, or any UI element from users who lack
 * a given permission.
 *
 * @example
 * <PermissionGate permission="customers.write">
 *   <Button onClick={handleAdd}>Add Customer</Button>
 * </PermissionGate>
 */
export function PermissionGate({ permission, children, fallback = null }: PermissionGateProps) {
  const { hasPermission } = usePermissions();
  return hasPermission(permission) ? <>{children}</> : <>{fallback}</>;
}
